// PreviewCandidateDetails_new.jsx. This file's printable receipt payload (#outerBorder/
// #divReceiptPrint, captured by html2canvas/jspdf) is deliberately left untouched — see that file's
// own Stage 3.5 comment. Only the page chrome OUTSIDE the printable payload (page title, action
// button row, and the "Confirm and Proceed" Modal) is migrated here. Every entry below is an
// extraction of text that was already bilingual in the file — zero new translation authored, since
// this outer chrome already had Marathi for every string.
//
// Not consumed via src/modules/i18n/dictionary.js's DICTIONARY object directly — imported and merged
// into the `preview` namespace by whatever aggregates modules/i18n/pages/*.js (kept as a standalone
// file per this migration's convention rather than editing dictionary.js itself).
const preview = {
  pageTitle: { en: 'Preview Of Candidate Details', mr: 'उमेदवाराच्या तपशीलांचे पूर्वावलोकन' },
  cancel: { en: 'Cancel', mr: 'रद्द करा' },
  previous: { en: 'Previous', mr: 'मागे' },
  saveNext: { en: 'Save & Next', mr: 'जतन करा आणि पुढे जा' },
  printDownloadApplication: { en: 'Print / Download Application', mr: 'अर्ज प्रिंट करा / डाउनलोड करा' },
  confirmAndProceed: { en: 'Confirm and Proceed', mr: 'पुष्टी करा आणि पुढे जा' },
  proceed: { en: 'Proceed', mr: 'पुढे जा' },
  reviewBeforeProceeding: {
    en: 'Please review your application details above before proceeding.',
    mr: 'पुढे जाण्यापूर्वी कृपया वरील अर्जाचे तपशील तपासा.'
  },

  // Phase R6.5 (Language-Configurable Application Receipt): the printable #divReceiptPrint payload
  // (rasterized by onPrint into the downloaded PDF) was previously hardcoded bilingual JSX, completely
  // bypassing this file's own Text/resolveText migration and the Admin-configured PortalLanguage
  // setting — meaning the PDF always showed Marathi even when English Only was configured. Every entry
  // below is an EXTRACTION of text that was already bilingual in the JSX (zero new translation
  // authored), now wired through <Text k="preview.*"/> so the receipt follows the same authoritative
  // language source as the rest of the candidate journey. Sub-fields that were already English-only in
  // the original JSX (Village/City, Taluka, Dist, Pin Code, State, Driving Licence No., Yes/N/A) are
  // kept English-only here too — no new translation invented for content that never had one.
  personalInfoSectionTitle: { en: 'Step 1 - Personal Information', mr: 'वैयक्तिक माहिती' },
  fullNameLabel: { en: 'Full Name', mr: 'पूर्ण नाव' },
  fathersNameLabel: { en: "Father's Name", mr: 'वडिलांचे नाव' },
  mothersNameLabel: { en: "Mother's Name", mr: 'आईचे नाव' },
  aadhaarNumberLabel: { en: 'Aadhaar Number', mr: 'आधार क्रमांक' },
  emailIdLabel: { en: 'Email id', mr: 'ई - मेल आयडी' },
  mobileNoLabel: { en: 'Mobile No.', mr: 'मोबाईल क्र.' },
  addressContactInfoLabel: { en: 'Address & Contact Info', mr: 'पत्ता आणि संपर्क माहिती' },
  villageCityLabel: { en: 'Village/City' },
  talukaLabel: { en: 'Taluka' },
  districtLabel: { en: 'Dist' },
  pinCodeLabel: { en: 'Pin Code' },
  stateLabel: { en: 'State' },
  genderLabel: { en: 'Gender', mr: 'लिंग' },
  dateOfBirthColonLabel: { en: 'Date Of Birth', mr: 'जन्मतारीख' },
  maritalStatusLabel: { en: 'Marital Status', mr: 'वैवाहिक स्थिती' },
  drivingLicenceNoLabel: { en: 'Driving Licence No.' },
  disabilityCategoryNameLabel: { en: 'Disability Category Name', mr: 'अपंगत्व श्रेणीचे नाव' },
  isScribeRequiredLabel: { en: 'Is Scribe Required?', mr: 'लेखनिक आवश्यक आहे का?' },
  existingEmployeeLabel: { en: 'Existing Employee', mr: 'विद्यमान कर्मचारी' },
  qualificationSectionTitle: { en: 'Step 1 - Candidate Qualification & Experience Details', mr: 'उमेदवाराची पात्रता आणि अनुभव तपशील' },
  educationalDetailsHeading: { en: 'Educational Details', mr: 'शैक्षणिक तपशील' },
  educationColumnHeader: { en: 'Education', mr: 'शिक्षण' },
  specialisationColumnHeader: { en: 'Specialisation', mr: 'विशेषज्ञता' },
  boardUniversityColumnHeader: { en: 'Name of Board/University', mr: 'बोर्ड/विद्यापीठ/संस्थेचे नाव' },
  yearOfPassingColumnHeader: { en: 'Year Of Passing', mr: 'उत्तीर्ण वर्ष' },
  marksPercentColumnHeader: { en: 'Marks (%)', mr: 'ग्रेड/टक्के' },
  classColumnHeader: { en: 'Class', mr: 'वर्ग' },
  typingSkillHeading: { en: 'Other Skill details (Typing)', mr: 'इतर कौशल्य तपशील (टायपिंग)' },
  marathiTyping30Label: { en: 'Marathi Typing (Speed 30)', mr: 'मराठी टायपिंग (वेग ३०)' },
  englishTyping40Label: { en: 'English Typing (Speed 40)', mr: 'इंग्रजी टायपिंग (वेग 40)' },
  shortHandHeading: { en: 'Short Hand Details', mr: 'लघुलेखन' },
  wpm100Label: { en: '100 Words/Minutes (Marathi/English)', mr: '100 शब्द/मिनिटे (मराठी/इंग्रजी)' },
  wpm120Label: { en: '120 Words/Minutes (Marathi/English)', mr: '120 शब्द/मिनिटे (मराठी/इंग्रजी)' },
  workExperienceHeading: { en: 'Work Experience', mr: 'कामाचा अनुभव' },
  organizationNameColumnHeader: { en: 'Organization Name', mr: 'संस्थेचे नाव' },
  designationColumnHeader: { en: 'Designation', mr: 'पदनाम' },
  fromDateColumnHeader: { en: 'From Date', mr: 'या तारखेपासून' },
  toDateColumnHeader: { en: 'To Date', mr: 'या तारखेपर्यंत' },
  feesDetailsHeading: { en: 'Fees Details', mr: 'फी तपशील' },
  examinationFeeLabel: { en: 'Examination Fee', mr: 'परीक्षा शुल्क' }
};

export default preview;
